import { useMemo, useState } from 'react';
import { Box, Button, Input, InputGroup, InputLeftElement, Spinner, Text, VStack } from '@chakra-ui/react';
import { MdSearch } from 'react-icons/md';
import type { GuideEntry, GuideEntryKind } from '@/lib/sdk/ai-guide';

const KIND_ORDER: { kind: GuideEntryKind; label: string }[] = [
  { kind: 'capability', label: 'Features' },
  { kind: 'recipe', label: 'Recipes' },
  { kind: 'concept', label: 'Concepts' },
  { kind: 'unsupported', label: 'Not supported' },
];

/** Entries a group shows before its "Show more" toggle */
const COLLAPSED_COUNT = 5;

interface GuideEntryListProps {
  entries: GuideEntry[] | undefined;
  isLoading: boolean;
  selectedId: string | null;
  onSelect: (entryId: string) => void;
}

/** The guide's entries grouped by kind, filtered by a search over title and summary */
export function GuideEntryList({ entries, isLoading, selectedId, onSelect }: GuideEntryListProps) {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Partial<Record<GuideEntryKind, boolean>>>({});
  const isSearching = search.trim() !== '';

  const groups = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matching = (entries ?? []).filter(
      (entry) => !query || entry.title.toLowerCase().includes(query) || entry.summary.toLowerCase().includes(query)
    );
    return KIND_ORDER.map(({ kind, label }) => ({
      kind,
      label,
      entries: matching.filter((entry) => entry.kind === kind).sort((a, b) => a.title.localeCompare(b.title)),
    })).filter((group) => group.entries.length > 0);
  }, [entries, search]);

  return (
    <VStack align="stretch" spacing={3} data-testid="guide-entry-list">
      <InputGroup size="sm">
        <InputLeftElement pointerEvents="none">
          <MdSearch />
        </InputLeftElement>
        <Input
          placeholder="Search the guide"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          data-testid="guide-search"
        />
      </InputGroup>

      {isLoading && <Spinner size="sm" />}
      {!isLoading && groups.length === 0 && (
        <Text fontSize="sm" color="text-secondary" data-testid="guide-search-empty">
          No entries match.
        </Text>
      )}

      {groups.map((group) => {
        // A search shows every match, and the open entry is never hidden
        const selectedIndex = group.entries.findIndex((entry) => entry.id === selectedId);
        const isOpen = expanded[group.kind] || isSearching || selectedIndex >= COLLAPSED_COUNT;
        const hiddenCount = group.entries.length - COLLAPSED_COUNT;
        const shown = isOpen ? group.entries : group.entries.slice(0, COLLAPSED_COUNT);
        return (
          <Box key={group.kind}>
            <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color="text-secondary" mb={1}>
              {group.label}
            </Text>
            <VStack align="stretch" spacing={0}>
              {shown.map((entry) => (
                <Button
                  key={entry.id}
                  size="sm"
                  variant={entry.id === selectedId ? 'solid' : 'ghost'}
                  justifyContent="flex-start"
                  fontWeight="normal"
                  whiteSpace="normal"
                  textAlign="left"
                  height="auto"
                  py={1.5}
                  onClick={() => onSelect(entry.id)}
                  data-testid={`guide-entry-${entry.id}`}
                >
                  {entry.title}
                </Button>
              ))}
              {hiddenCount > 0 && !isSearching && selectedIndex < COLLAPSED_COUNT && (
                <Button
                  size="xs"
                  variant="link"
                  alignSelf="flex-start"
                  mt={1}
                  ml={3}
                  onClick={() => setExpanded((previous) => ({ ...previous, [group.kind]: !isOpen }))}
                  data-testid={`guide-group-toggle-${group.kind}`}
                >
                  {isOpen ? 'Show less' : `Show ${hiddenCount} more`}
                </Button>
              )}
            </VStack>
          </Box>
        );
      })}
    </VStack>
  );
}
